import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from '@/context/AuthContext';
import { MessageCircle, Heart, Reply, Send, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { getMemberDisplayName } from '@/utils/memberUtils';

interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  author_name: string;
  content: string;
  created_at: string;
}

interface Post {
  id: string;
  user_id: string;
  author_name: string;
  content: string;
  created_at: string;
  likes: number;
  comments: number;
  liked_by_me?: boolean;
  comments_list?: Comment[];
}

interface SinglesForumProps {
  isAdmin: boolean;
}

const SinglesForum = ({ isAdmin }: SinglesForumProps) => {
  const { user } = useAuth();
  const [memberData, setMemberData] = useState<any>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPost, setNewPost] = useState('');
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [expandedPosts, setExpandedPosts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  
  // Add a members cache
  const [membersCache, setMembersCache] = useState<Record<string, any>>({});
  
  // Fetch member data when user is available
  useEffect(() => {
    if (user) {
      fetchMemberData();
    }
  }, [user]);
  
  // Fetch member profile data
  const fetchMemberData = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      setMemberData(data);
    } catch (err) {
      console.error('Error fetching member data:', err);
    }
  };
  
  useEffect(() => {
    fetchPosts();
    
    // Set up real-time listener for new posts
    const channel = supabase
      .channel('singles-forum-changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'singles_forum_posts' }, 
        () => {
          fetchPosts();
        })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  
  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all posts
      const { data: postsData, error: postsError } = await supabase
        .from('singles_forum_posts')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (postsError) throw postsError;
      
      // If user is logged in, check which posts they've liked
      let likedPosts: Record<string, boolean> = {};
      if (user) {
        const { data: likesData } = await supabase
          .from('singles_forum_likes')
          .select('post_id')
          .eq('user_id', user.id);
          
        if (likesData) {
          likesData.forEach(like => {
            likedPosts[like.post_id] = true;
          });
        }
      }
      
      // Get unique user IDs from all posts to fetch their display names
      const userIds = [...new Set((postsData || []).map(post => post.user_id))];
      
      // Fetch member data for all post authors in a single query
      const { data: memberData, error: memberError } = await supabase
        .from('community_members')
        .select('id, nickname, full_name')
        .in('id', userIds);
      
      if (memberError) {
        console.error('Error fetching member data:', memberError);
      }
      
      // Create a lookup map for member data
      const memberMap: Record<string, any> = {};
      if (memberData) {
        memberData.forEach(member => {
          memberMap[member.id] = member;
        });
      }
      
      // Cache the member data for later use
      setMembersCache(memberMap);
      
      // Format posts and add liked_by_me flag and proper author names
      const postsWithLikes = (postsData || []).map(post => {
        // Get the member data for this post's author
        const member = memberMap[post.user_id];
        
        // Use the correct author name (nickname or full_name or fallback)
        const authorName = member 
          ? (member.nickname || member.full_name) 
          : post.author_name || `User ${post.user_id.substring(0, 6)}`;
        
        return {
          ...post,
          author_name: authorName, // Override with correct name
          liked_by_me: likedPosts[post.id] || false,
          comments_list: []
        };
      });
      
      setPosts(postsWithLikes);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchCommentsForPost = async (postId: string) => {
    try {
      const { data: commentsData, error: commentsError } = await supabase
        .from('singles_forum_comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
        
      if (commentsError) throw commentsError;
      
      // Get unique user IDs from all comments
      const commentUserIds = [...new Set((commentsData || []).map(comment => comment.user_id))];
      
      // Fetch any missing member data for comment authors
      let missingUserIds = commentUserIds.filter(id => !membersCache[id]);
      
      if (missingUserIds.length > 0) {
        const { data: commentMemberData } = await supabase
          .from('community_members')
          .select('id, nickname, full_name')
          .in('id', missingUserIds);
          
        if (commentMemberData && commentMemberData.length > 0) {
          // Update the members cache with new data
          setMembersCache(prev => {
            const updated = { ...prev };
            commentMemberData.forEach(member => {
              updated[member.id] = member;
            });
            return updated;
          });
        }
      }
      
      // Format comments with correct author names
      const formattedComments = (commentsData || []).map(comment => {
        const member = membersCache[comment.user_id];
        
        // Use the correct author name (nickname or full_name or fallback)
        const authorName = member 
          ? (member.nickname || member.full_name) 
          : comment.author_name || `User ${comment.user_id.substring(0, 6)}`;
        
        return {
          ...comment,
          author_name: authorName // Override with correct name
        };
      });
      
      // Update the posts with the formatted comments
      setPosts(currentPosts => 
        currentPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              comments_list: formattedComments
            };
          }
          return post;
        })
      );
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  const handleSubmitPost = async () => {
    if (!user) {
      toast.error('Please sign in to post');
      return;
    }
    
    if (!newPost.trim()) {
      toast.error('Please introduce yourself before posting');
      return;
    }
    
    try {
      // Get member data if not already loaded
      if (!memberData) {
        await fetchMemberData();
      }
      
      // Use nickname or full_name from community_members
      const displayName = memberData?.nickname || memberData?.full_name || 'Anonymous';
      
      const newPostData = {
        user_id: user.id,
        author_name: displayName,
        content: newPost,
        likes: 0,
        comments: 0
      };
      
      const { data, error } = await supabase
        .from('singles_forum_posts')
        .insert(newPostData)
        .select();
        
      if (error) throw error;
      
      setNewPost('');
      toast.success('Your introduction has been shared with the community!');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save your post');
    }
  };
  
  const handleLikePost = async (postId: string, isLiked: boolean) => {
    if (!user) {
      toast.error('Please sign in to like posts');
      return;
    }
    
    try {
      // Optimistically update the UI
      setPosts(posts.map(post => 
        post.id === postId ? { 
          ...post, 
          likes: isLiked ? post.likes - 1 : post.likes + 1,
          liked_by_me: !isLiked
        } : post
      ));
      
      if (isLiked) {
        // Remove like
        await supabase
          .from('singles_forum_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);
          
        // Update post likes count
        await supabase
          .from('singles_forum_posts')
          .update({ likes: posts.find(p => p.id === postId)!.likes - 1 })
          .eq('id', postId);
      } else {
        // Add like
        await supabase
          .from('singles_forum_likes')
          .insert({
            post_id: postId,
            user_id: user.id
          });
          
        // Update post likes count
        await supabase
          .from('singles_forum_posts')
          .update({ likes: posts.find(p => p.id === postId)!.likes + 1 })
          .eq('id', postId);
      }
    } catch (error) {
      console.error('Error updating like:', error);
      toast.error('Failed to update like');
      
      // Revert the optimistic update on error
      fetchPosts();
    }
  };
  
  const toggleCommentInput = (postId: string) => {
    setActiveCommentId(activeCommentId === postId ? null : postId);
    setCommentText('');
  };

  const handleSubmitComment = async (postId: string) => {
    if (!user) {
      toast.error('Please sign in to comment');
      return;
    }
    
    if (!commentText.trim()) {
      toast.error('Please write something before commenting');
      return;
    }

    try {
      // Get member data if not already loaded
      if (!memberData) {
        await fetchMemberData();
      }
      
      // Use nickname or full_name from community_members
      const displayName = memberData?.nickname || memberData?.full_name || 'Anonymous';
      
      const newComment = {
        post_id: postId,
        user_id: user.id,
        author_name: displayName,
        content: commentText
      };
      
      const { data, error } = await supabase
        .from('singles_forum_comments')
        .insert(newComment)
        .select();
        
      if (error) throw error;
      
      // Update post comments count
      await supabase
        .from('singles_forum_posts')
        .update({ 
          comments: posts.find(p => p.id === postId)!.comments + 1 
        })
        .eq('id', postId);
      
      setCommentText('');
      setActiveCommentId(null);
      
      // Add the new post to expanded posts to show comments automatically
      if (!expandedPosts.includes(postId)) {
        setExpandedPosts([...expandedPosts, postId]);
      }
      
      // Fetch updated comments
      fetchCommentsForPost(postId);
      
      // Also update local state
      setPosts(posts => posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            comments: post.comments + 1
          };
        }
        return post;
      }));
      
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Failed to save your comment');
    }
  };

  const toggleExpandComments = (postId: string) => {
    if (expandedPosts.includes(postId)) {
      setExpandedPosts(expandedPosts.filter(id => id !== postId));
    } else {
      setExpandedPosts([...expandedPosts, postId]);
      fetchCommentsForPost(postId);
    }
  };
  
  const handleDeletePost = async () => {
    if (!postToDelete || !user) return;
    
    try {
      const { error } = await supabase
        .from('singles_forum_posts')
        .delete()
        .eq('id', postToDelete);
      
      if (error) throw error;
      
      // Update UI
      setPosts(posts.filter(post => post.id !== postToDelete));
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    } finally {
      setPostToDelete(null);
    }
  };
  
  const canDeletePost = (postUserId: string) => {
    return user && (user.id === postUserId || isAdmin);
  };
  
  return (
    <div className="space-y-6">
      <Card className="border-pink-100 bg-gradient-to-r from-pink-50 to-white shadow-md">
        <CardHeader className="bg-gradient-to-r from-pink-100 to-pink-50 rounded-t-lg">
          <CardTitle className="text-pink-700">Singles Forum</CardTitle>
          <CardDescription className="text-pink-600">Connect with other singles in the Yoruba community</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Create Post */}
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm border border-pink-100">
            <Textarea 
              placeholder="Introduce yourself and what you're looking for in a partner..." 
              className="mb-2 border-pink-200 focus:border-pink-400 focus:ring focus:ring-pink-200 focus:ring-opacity-50" 
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
            />
            <div className="flex justify-end">
              <Button 
                onClick={handleSubmitPost} 
                className="bg-pink-600 hover:bg-pink-700 text-white"
                disabled={!user}
              >
                Share Introduction
              </Button>
            </div>
            {!user && (
              <p className="text-xs text-pink-500 mt-2 text-right">Please sign in to post</p>
            )}
          </div>
          
          {/* Posts Feed */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-pink-300 border-t-pink-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-pink-600">Loading community posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-8 bg-pink-50 rounded-lg">
                <p className="text-pink-600 mb-2">No introductions yet</p>
                <p className="text-gray-600">Be the first to introduce yourself to the community!</p>
              </div>
            ) : (
              <AnimatePresence>
                {posts.map(post => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="border-pink-100 hover:shadow-md transition-all duration-300">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="border-2 border-pink-200">
                            <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-400 text-white">{post.author_name.substring(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <h4 className="font-medium text-pink-800">{post.author_name}</h4>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-pink-500">{formatDate(post.created_at)}</span>
                                
                                {canDeletePost(post.user_id) && (
                                  <button 
                                    className="text-gray-400 hover:text-red-500 transition-colors focus:outline-none"
                                    onClick={() => setPostToDelete(post.id)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-700">{post.content}</p>
                            <div className="flex items-center gap-4 mt-4">
                              <button 
                                className={`flex items-center gap-1 text-sm transition-colors ${post.liked_by_me ? 'text-pink-600' : 'text-gray-500 hover:text-pink-600'}`}
                                onClick={() => handleLikePost(post.id, post.liked_by_me || false)}
                                disabled={!user}
                              >
                                <Heart size={16} fill={post.liked_by_me ? "#db2777" : "none"} />
                                <span>{post.likes}</span>
                              </button>
                              <button 
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-pink-600 transition-colors"
                                onClick={() => toggleExpandComments(post.id)}
                              >
                                <MessageCircle size={16} />
                                <span>{post.comments}</span>
                              </button>
                              <button 
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-pink-600 transition-colors"
                                onClick={() => toggleCommentInput(post.id)}
                                disabled={!user}
                              >
                                <Reply size={16} />
                                <span>Reply</span>
                              </button>
                            </div>
                            
                            {/* Comment Input */}
                            {activeCommentId === post.id && (
                              <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 flex gap-2"
                              >
                                <Input 
                                  placeholder="Write a reply..." 
                                  value={commentText}
                                  onChange={(e) => setCommentText(e.target.value)}
                                  className="flex-1 border-pink-200 focus:border-pink-400"
                                />
                                <Button 
                                  size="sm" 
                                  onClick={() => handleSubmitComment(post.id)}
                                  className="bg-pink-600 hover:bg-pink-700"
                                >
                                  <Send size={16} />
                                </Button>
                              </motion.div>
                            )}
                            
                            {/* Comments List */}
                            {expandedPosts.includes(post.id) && post.comments_list && post.comments_list.length > 0 && (
                              <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-4 space-y-3 pl-2 border-l-2 border-pink-100"
                              >
                                {post.comments_list.map(comment => (
                                  <div key={comment.id} className="bg-pink-50 p-3 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <Avatar className="h-6 w-6 border border-pink-200">
                                        <AvatarFallback className="text-xs bg-gradient-to-br from-purple-400 to-pink-400 text-white">
                                          {comment.author_name.substring(0, 2).toUpperCase()}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <div className="flex gap-2 items-center">
                                          <span className="text-xs font-medium text-pink-700">{comment.author_name}</span>
                                          <span className="text-xs text-pink-400">{formatDate(comment.created_at)}</span>
                                        </div>
                                        <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!postToDelete} onOpenChange={(open) => !open && setPostToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePost}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SinglesForum;
